<template>
  <v-dialog :value="value" max-width="760px" @input="updateDialog">
    <v-card v-if="product" class="product-image-preview-card rounded-xl">
      <div class="product-image-preview-header">
        <div class="product-image-preview-title">{{ product.name }}</div>

        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4 product-image-preview-content">
        <v-img
          v-if="previewUrl"
          :src="previewUrl"
          contain
          max-height="620"
          @error="handlePreviewError"
        />

        <v-img
          v-else
          src="@/assets/images/photoComingSoon.png"
          contain
          max-height="420"
        />

        <div class="product-image-preview-source">
          <v-chip
            small
            label
            :color="sourceColor"
            :dark="previewType !== 'thumbnail'"
          >
            <v-icon small left>{{ sourceIcon }}</v-icon>
            {{ sourceLabel }}
          </v-chip>

          <span v-if="previewType === 'thumbnail'">
            The original image is unavailable, so the product thumbnail is
            shown.
          </span>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ProductImagePreviewDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      previewUrl: null,
      previewType: null,
    };
  },

  computed: {
    originalUrl() {
      if (!this.product) return null;
      return (
        this.product.imageUrl ||
        this.product.imageURL ||
        this.product.image ||
        null
      );
    },

    thumbnailUrl() {
      if (!this.product) return null;
      return (
        this.product.thumbnailUrl ||
        this.product.thumbnailURL ||
        this.product.thumbnail ||
        this.product.thumbUrl ||
        this.product.thumb ||
        null
      );
    },

    sourceLabel() {
      if (this.previewType === "original") return "Original image";
      if (this.previewType === "thumbnail") return "Thumbnail fallback";
      return "Image unavailable";
    },

    sourceColor() {
      if (this.previewType === "original") return "success";
      if (this.previewType === "thumbnail") return "warning";
      return "grey";
    },

    sourceIcon() {
      if (this.previewType === "original") return "mdi-image-check";
      if (this.previewType === "thumbnail") {
        return "mdi-image-size-select-small";
      }
      return "mdi-image-off";
    },
  },

  watch: {
    product: {
      immediate: true,
      handler() {
        this.setInitialPreview();
      },
    },
    value(isOpen) {
      if (isOpen) this.setInitialPreview();
    },
  },

  methods: {
    setInitialPreview() {
      if (this.originalUrl) {
        this.previewUrl = this.originalUrl;
        this.previewType = "original";
        return;
      }

      if (this.thumbnailUrl) {
        this.previewUrl = this.thumbnailUrl;
        this.previewType = "thumbnail";
        return;
      }

      this.previewUrl = null;
      this.previewType = null;
    },

    handlePreviewError() {
      if (
        this.previewType === "original" &&
        this.thumbnailUrl &&
        this.thumbnailUrl !== this.previewUrl
      ) {
        this.previewUrl = this.thumbnailUrl;
        this.previewType = "thumbnail";
        return;
      }

      this.previewUrl = null;
      this.previewType = null;
    },

    updateDialog(isOpen) {
      this.$emit("input", isOpen);
    },

    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.product-image-preview-card {
  overflow: hidden;
}

.product-image-preview-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.product-image-preview-title {
  font-size: 20px;
  font-weight: 900;
  color: #0f172a;
}

.product-image-preview-content {
  background: #f8fafc;
}

.product-image-preview-source {
  min-height: 34px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #64748b;
  font-size: 12px;
  text-align: center;
}
</style>
